<template>
  <div class="auth-page">
    <UcariHeader />
    <main class="system-page"><section class="system-card system-card--form">
    <span class="eyebrow">Partner invitation</span>
    <h1>Complete your account</h1>
    <p>Set a secure password to accept your invitation and continue to the partner portal.</p>

    <div v-if="loading" class="uk-margin"><div uk-spinner></div></div>

    <div v-else>
      <div v-if="message" class="uk-alert-success" uk-alert>{{ message }}</div>
      <div v-if="error" class="uk-alert-danger" uk-alert>{{ error }}</div>

      <div v-if="emailMasked && !completed">
        <p>Invite for <strong>{{ emailMasked }}</strong></p>

        <form @submit.prevent="completeInvite" class="embedded-form">
          <div class="uk-margin">
            <label for="newPassword">Password</label>
            <div class="uk-inline">
              <input
                :type="showPassword ? 'text' : 'password'"
                class="uk-input"
                id="newPassword"
                v-model="newPassword"
                @input="handlePasswordInput"
                required
              />
              <button
                type="button"
                class="password-toggle"
                @click.prevent="togglePasswordVisibility"
              >
                {{ showPassword ? 'Hide' : 'Show' }}
              </button>
            </div>

            <div class="password-requirements uk-text-small uk-margin-small-top">
              <div><strong>Password must contain:</strong></div>
              <ul class="uk-list uk-list-bullet">
                <li :class="{ 'valid': passwordHasLowercase }">One lowercase letter</li>
                <li :class="{ 'valid': passwordHasUppercase }">One uppercase letter</li>
                <li :class="{ 'valid': passwordHasNumber }">One number</li>
                <li :class="{ 'valid': passwordHasSpecialChar }">One of these special characters: ! @ # $ % ^ & * ( ) - + { } | ?</li>
                <li :class="{ 'valid': passwordHasLength }">At least 8 characters</li>
              </ul>
            </div>
          </div>

          <!-- Confirm Password -->
          <div class="uk-margin">
            <label for="confirmPassword">Confirm Password</label>
            <div class="uk-inline">
              <input
                :type="showConfirmPassword ? 'text' : 'password'"
                class="uk-input"
                id="confirmPassword"
                v-model="confirmNewPassword"
                @input="handlePasswordInput"
                required
              />
              <button
                type="button"
                class="password-toggle"
                @click.prevent="toggleConfirmPasswordVisibility"
              >
                {{ showConfirmPassword ? 'Hide' : 'Show' }}
              </button>
            </div>
            <div class="uk-text-danger uk-margin-small-top" v-if="passwordMismatch">
              {{ passwordMismatch }}
            </div>
          </div>

          <button
            type="submit"
            class="uk-button uk-button-primary"
            :disabled="isFormInvalid || saving"
          >
            {{ saving ? 'Setting password…' : 'Set Password & Continue' }}
          </button>
        </form>
      </div>

      <div v-else-if="completed" class="uk-margin">
        <p>Your invite has been accepted and your password is set.</p>
        <button class="uk-button uk-button-primary" @click="goToPortal">Go to Portal</button>
      </div>

      <div v-else-if="error && expired">
        <p class="uk-margin">Your invite link has expired.</p>
        <!-- Optional: show a help link -->
        <a :href="supportLink" target="_blank" rel="noopener">Contact support</a>
      </div>
    </div>

    </section></main>
    <RetailerFooter />
  </div>
</template>

<script>
import axios from 'axios';
import UcariHeader from './components/UcariHeader.vue';
import RetailerFooter from './components/RetailerFooter.vue';
import { validatePassword, toggleVisibility } from '@/utils/common';

const API_BASE = process.env.VUE_APP_API_URL || '';

export default {
  name: 'RetailerInviteAccept',
  components: { UcariHeader, RetailerFooter },
  data() {
    return {
      token: this.$route.query.token || '',
      emailMasked: '',
      loading: true,
      saving: false,
      completed: false,
      expired: false,
      message: '',
      error: '',
      // password model (like your reset page)
      newPassword: '',
      confirmNewPassword: '',
      showPassword: false,
      showConfirmPassword: false,
      isPasswordInvalid: false,
      passwordMismatch: '',
      supportLink: 'mailto:care@ucari.com'
    };
  },
  computed: {
    isFormInvalid() {
      return (
        this.isPasswordInvalid ||
        !!this.passwordMismatch ||
        !this.newPassword ||
        !this.confirmNewPassword
      );
    },
    passwordHasLowercase() {
      return /[a-z]/.test(this.newPassword);
    },
    passwordHasUppercase() {
      return /[A-Z]/.test(this.newPassword);
    },
    passwordHasNumber() {
      return /\d/.test(this.newPassword);
    },
    passwordHasSpecialChar() {
      return /[!@#$%^&*()\-+{}|?]/.test(this.newPassword);
    },
    passwordHasLength() {
      return this.newPassword.length >= 8;
    }
  },
  async mounted() {
    if (!this.token) {
      this.error = 'Missing invite token.';
      this.loading = false;
      return;
    }
    try {
      const { data } = await axios.post(
        `${API_BASE}/api/retailers/accept/validate`,
        { token: this.token }
      );
      this.emailMasked = data.emailMasked || '';
    } catch (e) {
      const msg = e?.response?.data?.error || 'Invalid or expired invite.';
      this.error = msg;
      this.expired = /expired/i.test(msg);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    handlePasswordInput() {
      const { isPasswordInvalid, passwordMismatch } = validatePassword(
        this.newPassword,
        this.confirmNewPassword
      );
      this.isPasswordInvalid = isPasswordInvalid;
      this.passwordMismatch = passwordMismatch;
    },
    togglePasswordVisibility() {
      this.showPassword = toggleVisibility(this.showPassword);
    },
    toggleConfirmPasswordVisibility() {
      this.showConfirmPassword = toggleVisibility(this.showConfirmPassword);
    },
    async completeInvite() {
      if (this.isFormInvalid) {
        this.error = 'Please correct the errors in the form';
        return;
      }

      this.saving = true;
      this.error = '';

      try {
        const { data } = await axios.post(
          `${API_BASE}/api/retailers/accept/complete`,
          { token: this.token, password: this.newPassword }
        );

        // Backend now returns: accessToken, cid, squareCustomerId, redirectUrl
        const accessToken = data.accessToken || data.token || '';
        const cid = data.cid || data.retailerCustomerId || '';
        const squareCustomerId = data.squareCustomerId || '';

        // 🔐 Mirror what your normal login does
        if (accessToken) {
          localStorage.setItem('accessToken', accessToken);
          axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        }

        if (cid) {
          localStorage.setItem('cid', cid);
        }

        if (squareCustomerId) {
          localStorage.setItem('squareCustomerId', squareCustomerId);
        }

        const redirect = data.redirectUrl || '/shop';

        this.completed = true;
        this.message = 'Password set successfully. Redirecting…';

        // You can use router push (SPA)…
        this.$router.push(redirect);

        // …or if your app only reads localStorage on full load:
        // window.location.href = redirect;
      } catch (e) {
        const msg = e?.response?.data?.error || 'Failed to complete invite.';
        this.error = msg;
        this.expired = /expired/i.test(msg);
      } finally {
        this.saving = false;
      }
    },
    goToPortal() {
      // adjust to your retailer dashboard route if you have one
      this.$router.push({ name: 'HomePage' });
    }
  }
};
</script>

<style scoped>
.password-requirements li.valid {
  color: #1e87f0; /* uk-primary */
  font-weight: 600;
}
</style>
